import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { toggleEditMode, updateWidgetProperty } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from "components/Editor";
import { Container } from '../../core/container';
import { Property } from '../../../components/properties';
import { useChildrenWidgetSelected, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { Background } from 'components/Editor/components/properties/background';
import { BackgroundView } from '../../background';

import styles from './emptyMessage.module.scss';
import { getMediaInfo } from "actions/pageHealth";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const EmptyMessageView = React.memo(function EmptyMessage({ className, style, widgetProps, widgetRef, children }) {
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const parent = _.get(widgetProps, 'parent');
  const background = _.get(widgetProps, 'properties.desktop.background');

  const parentSelected = useWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  
  const widgetStyles = {
    ...style,
    marginLeft: 0
  };

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div
        id={id}
        ref={ref}
        style={widgetStyles}
        className={cx(className, styles.emptyMessage, 'widget', {
          [styles.stretched]: stretched,
          [styles.parentSelected]: parentSelected,
          [styles.partialSelection]: childrenSelection.selected,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={!childrenSelection.selected}
      >
        <BackgroundView background={background} />
        {React.Children.map(children,
          child => {
            return (
              React.cloneElement(child, {
                parentRef: ref,
                stretched: stretched,
                parentProps: widgetProps
              }, null)
            );
          })
        }

      </div>
    </Container>
  )
})

const EmptyMessageWidget = React.forwardRef((props, ref) => {
  return <EmptyMessageView {...props} ref={ref} />
});

const EmptyMessageWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }
    </>
  );
}

EmptyMessageWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <EmptyMessageWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

EmptyMessageWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background
      }
    }
  }
}

EmptyMessageWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

EmptyMessageWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    cloneable: false,
    deletable: false,
    renderDirections: ['s'],
    onResize: (widget, layout, breakpoint) => {
      const stretched = _.get(widget, 'properties.desktop.stretched');
      const frame = new Frame(layout);
      if (stretched && breakpoint === 'desktop') {
        const margin = _.get(widget, 'properties.desktop.margin', 0);
        const width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        frame.set('width', width);
      }

      return frame.get();
    },
    onReparent: (widget, parent) => {
      return false
    }
  }
}

export { EmptyMessageWidget }